import { create } from "zustand";
import { supabase } from "../lib/supabase.js";
import { translate as t } from "../i18n/translate.js";
import { syncJourneyOnLogin } from "../lib/journey.js";

// ============================================================
// Types
// ============================================================

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  avatarUrl: string | null;
  createdAt: string;
  lastLoginAt: string | null;
}

export type UsernameStatus = "idle" | "checking" | "available" | "taken" | "invalid";

const USERNAME_REGEX = /^[a-zA-Z0-9]{1,10}$/;

interface AuthStore {
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;

  initialize(): Promise<void>;
  register(username: string, email: string, password: string): Promise<void>;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  checkUsername(username: string): Promise<UsernameStatus>;
  /** E-Mail ändern — Supabase sendet Bestätigungs-Mail an neue Adresse */
  updateEmail(newEmail: string): Promise<void>;
  /** Passwort ändern — Re-Auth mit aktuellem Passwort, dann Update */
  updatePassword(currentPassword: string, newPassword: string): Promise<void>;
  clearError(): void;
}

// ============================================================
// Helpers
// ============================================================

async function fetchProfile(userId: string): Promise<AuthUser | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("username, email, avatar_url, created_at, last_login_at")
    .eq("id", userId)
    .single();

  if (error || !data) return null;

  return {
    id: userId,
    email: (data.email as string | null) ?? "",
    username: data.username as string,
    avatarUrl: (data.avatar_url as string | null) ?? null,
    createdAt: data.created_at as string,
    lastLoginAt: (data.last_login_at as string | null) ?? null,
  };
}

async function recordLogin(userId: string): Promise<void> {
  // Login-Session anlegen
  await supabase.from("login_sessions").insert({ user_id: userId });

  // last_login_at aktualisieren
  await supabase
    .from("profiles")
    .update({ last_login_at: new Date().toISOString() })
    .eq("id", userId);
}

// ============================================================
// Store
// ============================================================

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  isLoading: true,
  error: null,

  // ----------------------------------------------------------
  // initialize — beim App-Start aufrufen
  // ----------------------------------------------------------
  async initialize() {
    set({ isLoading: true });

    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user) {
      const profile = await fetchProfile(session.user.id);
      if (profile) set({ user: profile });
    }

    // Auf Auth-Änderungen hören (Login via anderes Tab, Token-Refresh, etc.)
    supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        const profile = await fetchProfile(session.user.id);
        if (profile) set({ user: profile });
      } else {
        set({ user: null });
      }
    });

    set({ isLoading: false });
  },

  // ----------------------------------------------------------
  // register
  // ----------------------------------------------------------
  async register(username, email, password) {
    set({ isLoading: true, error: null });

    // Username validieren
    if (!USERNAME_REGEX.test(username)) {
      set({ isLoading: false, error: t("auth.error.usernameInvalid") });
      return;
    }

    // Supabase signUp — Trigger legt Profile automatisch an
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username, full_name: username } },
    });

    if (error) {
      set({ isLoading: false, error: error.message });
      return;
    }

    if (data.user) {
      try {
        await recordLogin(data.user.id);
      } catch (err) {
        console.error("[auth] recordLogin failed:", err);
      }
      try {
        await syncJourneyOnLogin(data.user.id);
      } catch (err) {
        console.error("[auth] syncJourneyOnLogin failed:", err);
      }
      try {
        const profile = await fetchProfile(data.user.id);
        if (profile) set({ user: profile });
      } catch (err) {
        console.error("[auth] fetchProfile failed:", err);
      }
    }

    set({ isLoading: false });
  },

  // ----------------------------------------------------------
  // login
  // ----------------------------------------------------------
  async login(email, password) {
    set({ isLoading: true, error: null });

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      set({ isLoading: false, error: t("auth.error.loginFailed") });
      return;
    }

    if (data.user) {
      try {
        await recordLogin(data.user.id);
      } catch (err) {
        console.error("[auth] recordLogin failed:", err);
      }
      try {
        await syncJourneyOnLogin(data.user.id);
      } catch (err) {
        console.error("[auth] syncJourneyOnLogin failed:", err);
      }
      try {
        const profile = await fetchProfile(data.user.id);
        if (profile) set({ user: profile });
      } catch (err) {
        console.error("[auth] fetchProfile failed:", err);
      }
    }

    set({ isLoading: false });
  },

  // ----------------------------------------------------------
  // logout
  // ----------------------------------------------------------
  async logout() {
    await supabase.auth.signOut();
    set({ user: null, error: null });
  },

  // ----------------------------------------------------------
  // checkUsername — für Live-Verfügbarkeits-Check
  // ----------------------------------------------------------
  async checkUsername(username): Promise<UsernameStatus> {
    if (!USERNAME_REGEX.test(username)) return "invalid";

    const { count } = await supabase
      .from("profiles")
      .select("id", { count: "exact", head: true })
      .eq("username", username);

    return count === 0 ? "available" : "taken";
  },

  // ----------------------------------------------------------
  // updateEmail — sendet Bestätigungs-Mail an neue Adresse
  // ----------------------------------------------------------
  async updateEmail(newEmail) {
    set({ isLoading: true, error: null });
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    if (error) {
      set({ isLoading: false, error: error.message });
      return;
    }
    set({ isLoading: false });
  },

  // ----------------------------------------------------------
  // updatePassword — Re-Auth mit aktuellem PW, dann neues PW setzen
  // ----------------------------------------------------------
  async updatePassword(currentPassword, newPassword) {
    set({ isLoading: true, error: null });

    const currentUser = get().user;
    if (!currentUser) {
      set({ isLoading: false, error: t("auth.error.notLoggedIn") });
      return;
    }

    // Re-Auth: sicherstellen dass aktuelles PW korrekt ist
    const { error: reAuthError } = await supabase.auth.signInWithPassword({
      email: currentUser.email,
      password: currentPassword,
    });
    if (reAuthError) {
      set({ isLoading: false, error: t("auth.error.wrongPassword") });
      return;
    }

    // Neues Passwort setzen
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) {
      set({ isLoading: false, error: updateError.message });
      return;
    }

    set({ isLoading: false });
  },

  // ----------------------------------------------------------
  clearError() {
    set({ error: null });
  },
}));
